import axios, {AxiosError, AxiosRequestConfig, AxiosResponse, ResponseType} from "axios";
import UyapHtmlParser from './parser/UyapHtmlParser';
import {getCachedItem, setCachedItem} from '../uyap-cache';
import {UyapRequestResponseHandler} from "./UyapRequestResponseHandler";
import {istekOlcumu} from "../IstekOlcumu";
import {
    yenidenBaglanmaBeklemesiMs,
    kotaDoluHatasiMi,
    kotaUyarisiGoster,
    yetkisizKaydet,
    devreKesiciSifirla,
    devreKesiciUyarisiGoster,
    durumMesajiGoster,
    DEVRE_KESICI_MESAJI
} from "../BaglantiKurtarma";
import {
    oturumKimligiUyusuyor,
    kimlikUyusmazligiBildir,
    KIMLIK_UYUSMAZLIGI_MESAJI
} from "../OturumKimligi";
import {PlayerDurum} from "../store/modules/GorevYoneticisiTypes";
import {SnapshotOkuyucu, SnapshotKaydi} from "../snapshot/SnapshotOkuyucu";
import {tazeMi, sonGorulme, TAZELIK_PENCERESI_MS, TAZELIK_PENCERESI_DK} from "../snapshot/tazelik";
import {borclununVergiNosu, cevaptanOgren, dosyaRefCoz} from "../snapshot/DosyaRefHafizasi";
import {TekilSnapshotYazici} from "../snapshot/TekilSnapshotYazici";
import app from "@/main";

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

export interface UyapPaginationResponse<T> {
    items: T[],
    currentPage: number,
    pageSize: number
}

export type RequestType = "json"|"formData"|"urlencoded";

export function isArray(data: any): boolean {
    return Array.isArray(data);
}

export function isUndefined(item: any): boolean {
    return typeof item == "undefined";
}


export abstract class UyapRequest<T, C> extends UyapRequestResponseHandler {
    axios = axios;
    ImzaClientAddr = "https://imza.eicrapro.com:6081/";
    htmlParser = UyapHtmlParser;
    message!: string;
    talepData!: T;
    /**
     * İstek sonrası bekleme (ms). execute() cevabı ya da hatayı aldıktan sonra, çağırana
     * dönmeden önce bu kadar bekler; önbellekten dönen cevapta bekleme olmaz.
     * 2000: UYAP'a binen yükü azaltmak için ekip kararı (DEV-3572); bir UYAP eşiğine ya da
     * denemeye dayanmaz. Alt sınıflar bu alanı ezebilir.
     */
    delayTime: number = 2000;
    uyapSessionCounter = 0;
    timeoutTryCounter = 0;

    private static sessionRecoveryLock: Promise<void> | null = null;
    private static htmlSessionCheckInProgress: boolean = false;
    htmlRetryDone: boolean = false;
    /** DEV-3084: zaman aşımı/5xx'te tekrar denemenin güvenli olduğu (ücretsiz, yan etkisiz) istekler true yapar. */
    protected geciciHatadaTekrarDene: boolean = false;
    private forceFresh: boolean = false;
    private isProbe: boolean = false;

    /**
     * B06 (düzeltme): eski TEK GLOBAL sayaç (`gorevBaglamiSayaci`) TÜM
     * uygulamaya kapı oluyordu — arka planda bir TakipSorgula görevi sürerken
     * kullanıcının ekrandan tetiklediği İLGİSİZ dosyaların tekil snapshot
     * yazımı (`snapshotaYaz()`, kaynak='manuel') da SESSİZCE atlanıyordu
     * (regresyon). Anahtar artık `dosya_ref`: `TakipSorgulaTaskHandler.handle()`
     * DosyaBul sonrası çözdüğü KENDİ dosyasının dosya_ref'ini bu haritaya +1
     * ekler, try/finally ile düşürür. `snapshotaYaz()` yalnız KENDİ isteğinin
     * (`this.talepData.dosyaId`'den çözülen) dosya_ref'i haritada >0 ise atlar
     * — başka bir dosya için gelen ekran isteği hiçbir zaman etkilenmez.
     * Değer (bool değil sayaç) aynı dosya için iç içe/örtüşen görev turlarını
     * (ör. aynı dosyayı hedefleyen iki TakipSorgula eşzamanlı koşarsa) erken
     * kapatmamak için. dosyaRef çözülemiyorsa (bağlam yok) haritaya hiç
     * girilmez — `snapshotaYaz()` o durumda ESKİ davranışta (tekil yazım
     * devam) kalır, sessiz susturma yapılmaz.
     */
    public static gorevBaglamindakiDosyalar: Map<string, number> = new Map();

    /**
     * Bu istekte cache okuması atlanır, UYAP'tan gelen taze cevap cache'e yazılır.
     * Kayıt silinmediği için istek hata verirse eldeki cache bozulmadan kalır.
     * Kullanım: this.$uyap.AvukatKayitliIbanList().fresh().run({})
     */
    public fresh(): this {
        this.forceFresh = true;
        return this;
    }

    /**
     * Bu istek bir YOKLAMADIR: "oturum ayakta mı" sorusuna cevap arar, iş yapmaz.
     * Oturum düşmüşse kurtarmaya girmez, hemen reddeder.
     *
     * Gerekçe: kurtarma bot istekleri için tasarlandı — turlar hâlinde socket'e
     * connectToUyap yayınlayıp 3/15/30 sn bekler. Yoklamayı da o yola sokmak iki
     * sorun üretiyordu: (1) cevap 48 sn gecikiyordu, (2) yoklamayı yapan zaten
     * connectToUyap'ın kendisi olduğu için kurtarma kendi kendini bekliyor ve
     * asla başarılı olamıyordu. Yoklamanın doğru cevabı "hayır"dır, beklemek değil.
     *
     * Kullanım: this.$uyap.KullaniciBilgileri().probe().run({})
     */
    public probe(): this {
        this.isProbe = true;
        return this;
    }

    async prepareRequestData(requestData: any): Promise<{ data: any, type: "urlencoded" | "formData" | "xml" | "json" }> {
        let data: any = requestData;
        let dataType: "urlencoded" | "formData" | "xml" | "json" = "urlencoded";
        if (typeof requestData.formData != "undefined") {
            data = requestData.data;
            dataType = "formData";
        } else if (typeof requestData.xml != "undefined") {
            data = requestData.data;
            dataType = "xml";
        } else if (typeof requestData.api != "undefined") {
            data = requestData.api;
            dataType = "json";
        }
        if(this.getRequestType() == "json"){
            data = requestData;
            dataType = "json";
        }
        return {
            data: data,
            type: dataType
        }
    }

    getHeaders(data: any) {
        let jsessionId = window.sessionStorage.getItem("JSESSIONID");
        let headers = {
            // "Token": jsessionId,//"JSESSIONID=0000Say0hve3UknBteFAizqT4Xt:1e3ae09ie",
            "Content-Type": "application/x-www-form-urlencoded"
        };
        if (typeof data != "undefined" && typeof data.formData != "undefined")
            headers["Content-Type"] = "multipart/form-data";
        if (typeof data != "undefined" && typeof data.xml != "undefined")
            headers["Content-Type"] = "application/xml;charset=UTF-8";
        if (typeof data != "undefined" && typeof data.api != "undefined")
            headers["Content-Type"] = "application/json;charset=UTF-8";
        //TODO GETREQUEST TYPE EKLENDI ABSTRACT BURALAR OPTİMİZE EDİLECEK STANDART OLACAK SUAN ZAMAN YOK
        if (typeof data != "undefined" && this.getRequestType() == "json")
            headers["Content-Type"] = "application/json;charset=UTF-8";
        return headers;
    }

    showSpinnerMessage(){
        //@ts-ignore
        if(typeof app.$spinner != "undefined"){
            //@ts-ignore
            app.$spinner.message(this.getSpinnerMessage(),this.getTimeoutSeconds());
        }
    }

    hideSpinnerMessage(){
        //@ts-ignore
        if(typeof app.$spinner != "undefined"){
            //@ts-ignore
            app.$spinner.hide();
        }
    }

    /**
     * Kimlik denetiminden muaf mı. Yalnızca oturumun kime ait olduğunu ÖĞRENEN
     * istek muaf olabilir (KullaniciBilgileri); aksi halde denetim kendi verisini
     * bekleyip kilitlenir.
     */
    protected kimlikDenetimindenMuaf(): boolean {
        return false;
    }

    /**
     * GÜVENLİK KAPISI: açık UYAP oturumu bu hesaba ait değilse istek GÖNDERİLMEZ.
     *
     * Denetim eskiden yalnızca vue-imza-app'in connectToUyap akışındaydı ve yalnız
     * "bağlanma" anında çalışıyordu. Oysa istekler icrapro-app'ten de doğrudan
     * gidiyor (aynı proxy, aynı cookie jar): hesabı A olan kullanıcı, B'nin
     * e-imzasıyla açılmış oturum üzerinden sorgu yapabiliyor, görev üretebiliyor,
     * hatta tevzi alabiliyordu. Kapı bu yüzden istek katmanında.
     *
     * Oturum TC'si state'ten okunuyor (iki uygulamada da uyapAvukatBilgileri).
     * Bilinmiyorsa denetim yapılmaz — eksik veri uyuşmazlık kanıtı değildir ve
     * KullaniciBilgileri henüz çalışmamış olabilir.
     */
    private kimlikKapisi(): void {
        if (this.kimlikDenetimindenMuaf()) {
            return;
        }
        const denetim = oturumKimligiUyusuyor({
            oturum: app.$store?.state?.uyapAvukatBilgileri?.tcKimlikNo,
            hesap: window.sessionStorage.getItem('tckn')
        });
        if (denetim.uyusuyor) {
            return;
        }
        // Bayrağı düşür: arayüz "bağlı" göstermeye devam etmemeli.
        app.$store.commit('setUyapConnection', false);
        kimlikUyusmazligiBildir(denetim.sebep ?? 'hesap');
        throw new Error(KIMLIK_UYUSMAZLIGI_MESAJI);
    }

    async execute(data: T): Promise<AxiosResponse> {
        // catch bloğundan da erişilebilmesi için try dışında tanımlı.
        let agSuresiMs = 0;
        let uykuSuresiMs = 0;
        this.kimlikKapisi();
        try{
            this.timeoutTryCounter++;
            let requestPayload = await this.prepareRequestData(data);
            let response;
            let axiosConfig = {
                url: this.getUrl(),
                method: this.getMethod(),
                headers: this.getHeaders(data),
                responseType: this.getResponseType(data),
                withCredentials: true,
                timeout: this.getTimeoutSeconds() * 1000
            } as AxiosRequestConfig;
            this.showSpinnerMessage();
            if (app.$store.getters.isEicraproDevMode) {
                await delay(this.delayTime);
                response = {
                    statusText: "OK",
                    status: 200,
                    data: this.getMockResponse(),
                } as AxiosResponse;
            } else {
                //let params =  new URLSearchParams(data)
                switch (this.getMethod()) {
                    case "GET":
                        axiosConfig.params = requestPayload.data;
                        break;
                    case "POST":
                        if (requestPayload.type == "formData") {
                            axiosConfig.data = requestPayload.data;
                        }
                        else if(requestPayload.type == "json"){
                            axiosConfig.data = JSON.stringify(requestPayload.data);
                        }
                        else {
                            axiosConfig.data = this.serialize(requestPayload.data);
                        }
                        break;
                }
                // Ağ süresi delay() hariç ölçülür.
                const agBaslangic = Date.now();
                try {
                    response = await this.axios(axiosConfig);
                } finally {
                    // finally: hata yolları da ölçüme girsin.
                    agSuresiMs = Date.now() - agBaslangic;
                }
                /*let delayTime = defaultDelayTime;
                if(typeof this.getDelayTime() != "undefined"){
                    delayTime = this.getDelayTime();
                }
                 */
                const uykuBaslangic = Date.now();
                await delay(this.delayTime);
                uykuSuresiMs = Date.now() - uykuBaslangic;
            }
            istekOlcumu.istekKaydet(this.metricName(), agSuresiMs, uykuSuresiMs);
            this.hideSpinnerMessage();
            return response;
        }catch (error) {
            istekOlcumu.istekKaydet(this.metricName(), agSuresiMs, uykuSuresiMs, error?.message ?? String(error));
            // Hata yolunda da tempo korunur. Basari yolundaki delay() burada
            // calismadigi icin 401 dongusu vaktiyle ag hiziyla (14,5 istek/sn)
            // donuyordu; bu bekleme, kurtarma mantigi ileride yine bozulsa bile
            // temponun delayTime'i asmasini yapisal olarak imkansiz kilar.
            await delay(this.delayTime);
            if(error.response?.status == 401){
                istekOlcumu.oturumKurtarmaKaydet();
                // Kisa surede cok sayida 401 = ban riski; devre kesici son emniyet.
                if (yetkisizKaydet()) {
                    devreKesiciUyarisiGoster();
                    throw new Error(DEVRE_KESICI_MESAJI);
                }
                return  this.uyapSessionCheck(data);
            }
            // DEV-3084: 504 her istekte tekrar denenir (eski davranış). Zaman aşımı ve diğer 5xx yalnız
            // geciciHatadaTekrarDene=true isteklerde: ücretli sorgular (EGM, TAKBİS, banka…) zaman aşımında
            // UYAP'ta işlenmiş olabilir, tekrar denemek ücreti ikinci kez keser.
            const status = error.response?.status;
            const geciciHata = error.code === "ECONNABORTED" || status >= 500;
            if (status == 504 || (this.geciciHatadaTekrarDene && geciciHata)) {
                if(this.timeoutTryCounter <= 3 ){
                    app.$toast.warning("Uyap'a gönderilen istek zaman aşımına uğradı tekrar deneniyor...");
                    istekOlcumu.retryKaydet();
                    await delay(1000);
                    return this.execute(data);
                }
                this.timeoutTryCounter = 0;
                throw new Error(error.message);
            }

            throw new Error(error.message);
        }
    }

    /** Telemetri etiketi. constructor.name terser ile mangle olduğu için URL'den türetilir. */
    protected metricName(): string {
        try {
            const parcalar = this.getUrl().split('?')[0].split('/');
            return parcalar[parcalar.length - 1] || 'bilinmiyor';
        } catch (e) {
            return 'bilinmiyor';
        }
    }

    serialize(obj: any) {
        let str = [];
        for (let p in obj)
            if (obj.hasOwnProperty(p)) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        return str.join("&");
    }

    /**
     * B29: `isDataCached()`/`run()`'ın PAYLAŞTIĞI "cache/snapshot kapısı açık
     * mı" kontrolü — run()'daki `cacheArgs && !this.forceFresh` ve
     * `cacheArgs.active === true` kontrolleriyle BİREBİR AYNI, artık tek
     * yerden geçiyor.
     *
     * NEDEN: eskiden `isDataCached()` bu kontrolleri UYGULAMIYORDU — yalnız
     * `cacheArgs` (herhangi bir cache tanımı) varlığına bakıyordu. `active:
     * false` bildiren bir sınıf (ör. `SorguBakiyeYukle`) ya da `fresh()`
     * çağrılmış bir istek için bile `snapshotCevabiKur()`'a bakıp `true`
     * dönebilirdi; `DosyaBorclu.query*` bunu görüp kota kapısını (ücretli
     * sorgu onayı) YANLIŞLIKLA atlayabilirdi. kod inceleme (T2-39): bu risk
     * PASİF değil — `snapshotAlani()` bildiren 19 sınıftan biri olan
     * `BorcluIcraDosyasi`, `TopluHacizGonderTaskHandler` içinde `fresh()` ile
     * çağrılıyor; asıl garanti aşağıdaki `!this.forceFresh` kontrolünün
     * GERÇEKTEN uygulanmasıdır (bu kontrol kaldırılırsa B29'un düzelttiği
     * kota-atlama regresyonu geri gelir).
     */
    private cacheKapisiAcikMi(
        cacheArgs: { active: boolean, ttl: number, key: string } | null | undefined
    ): cacheArgs is { active: boolean, ttl: number, key: string } {
        return !!cacheArgs && cacheArgs.active === true && !this.forceFresh;
    }

    /**
     * F5 (KULLANICI KARARI, 9 Eyl 2026): `snapshotAlani()` bildiren sınıflarda
     * yerel IndexedDB yazımının etkin TTL'i — v1'de (B15) İstanbul gece
     * yarısına kalan süreyle kırpılıyordu (`tazelik.geceYarisinaKalanDakika()`,
     * KALDIRILDI); tazelik artık takvim gününe değil SAAT PENCERESİNE
     * dayandığı için gece yarısının bununla ilgisi kalmadı.
     *
     * İKİ DAL:
     *  - CANLI cevap (`snapshotKaynak` verilmez): pencere sabit 24 saat
     *    (`TAZELIK_PENCERESI_DK` = 1440) — `cacheArgs.ttl` bundan uzunsa
     *    kırpılır, kısaysa (bugün hiçbir sınıfta yok ama savunmalı) korunur.
     *  - SNAPSHOT'tan servis edilen cevap (`snapshotKaynak` = `snapshotCevabiKur()`
     *    çıktısı, `snapshotDamgasi`/`snapshotTazeKadar` etiketli): yerel kayıt
     *    sunucudaki kaydın taze KALDIĞI kadar tutulur — `tazeMi()` ile SİMETRİK
     *    ölçüt: `taze_kadar` çözülüyorsa `taze_kadar − şimdi`, çözülmüyorsa
     *    `24 saat − (şimdi − sonGorulme)`. Böylece EGM gibi 3 günde bir taze
     *    sayılan alanlarda yerel kayıt da 3 gün boyunca gereksiz yere
     *    yenilenmez; her iki durumda da EN AZ 1 dakika (`setCachedItem`'in
     *    ttl<=0'ı "hemen bayat" gibi davranmayabilir, bkz. `uyap-cache.ts`).
     *
     * `snapshotAlani()` bildirmeyen sınıflar (kota/gün kavramı olmayan
     * istekler, ör. dosya listesi) ETKİLENMEZ — `cache()` tanımları değişmez.
     */
    private etkinLocalTtl(ttlDakika: number, snapshotKaynak?: any | null): number {
        if (!this.snapshotAlani()) {
            return ttlDakika;
        }

        if (!snapshotKaynak) {
            return Math.min(ttlDakika, TAZELIK_PENCERESI_DK);
        }

        const simdi = Date.now();
        const tazeKadar = snapshotKaynak.snapshotTazeKadar ? new Date(snapshotKaynak.snapshotTazeKadar) : null;
        if (tazeKadar && !isNaN(tazeKadar.getTime())) {
            return Math.max(1, Math.ceil((tazeKadar.getTime() - simdi) / 60000));
        }

        const damgaMs = snapshotKaynak.snapshotDamgasi ? new Date(snapshotKaynak.snapshotDamgasi).getTime() : NaN;
        if (isNaN(damgaMs)) {
            // Damga da okunamıyorsa (beklenmez, savunma amaçlı): sabit pencereye düş.
            return Math.min(ttlDakika, TAZELIK_PENCERESI_DK);
        }

        return Math.max(1, Math.ceil((TAZELIK_PENCERESI_MS - (simdi - damgaMs)) / 60000));
    }

    /**
     * "Bu sorgu için ücretli koşuma gerek var mı?" sorusunun cevabı.
     *
     * `DosyaBorclu.query*` bu sonuca bakıp `kotaIslemleri()`ni ATLIYOR. Faz 4'te
     * sunucu snapshot'ı da bir cache katmanı olduğu için BURASI DA onu sormak
     * ZORUNDA: aksi halde snapshot taze ama IndexedDB boşken kota akışı boşuna
     * koşar, günün hakkı dolmuşsa "Sorgu kotası kalmadı" diye FIRLATIR ve
     * `run()` snapshot'a hiç ulaşamaz; barokarttan yükleme açıksa gereksiz
     * bakiye bile yükleyebilirdi.
     */
    public async isDataCached(data: T): Promise<boolean> {
        this.talepData = data;
        let cacheArgs = this.cache();
        if (!this.cacheKapisiAcikMi(cacheArgs)) {
            return false;
        }

        let cachedData = await getCachedItem<C>(cacheArgs.key);
        if (cachedData) {
            return true;
        }

        return (await this.snapshotCevabiKur()) !== null;
    }

    public run(data: T): Promise<C> {
        return new Promise<C>(async (resolve, reject) => {
            // Kapı burada da var: önbellekten dönen cevap execute()'a hiç uğramıyor
            // ve önbellekteki veri de başka bir avukatın oturumundan gelmiş olabilir.
            try {
                this.kimlikKapisi();
            } catch (e) {
                return reject(e);
            }
            this.talepData = data;
            this.uyapSessionCounter++;
            let cacheArgs = this.cache();
            if (this.cacheKapisiAcikMi(cacheArgs)) {
                let cachedData = await getCachedItem<C>(cacheArgs.key);
                if (cachedData) {
                    //console.log("RETURNED FROM CACHE STORAGE " + cacheArgs.key, cachedData);
                    let parsedResult = await this.parse(cachedData);
                    return resolve(parsedResult);
                }

                // Faz 4 (DEV-3216) — okuma sırasının İKİNCİ basamağı: yerel cache
                // yoksa/bayatsa sunucu snapshot'ı. Gece botunun (ayrı origin, ayrı
                // IndexedDB) topladığı veri burada ekrana düşer ve ücretli sorgu
                // hiç atılmaz. Sunucu erişilemezse `snapshotCevabiKur()` null
                // döner, zincir bugünkü gibi canlıya devam eder.
                const snapshotCevabi = await this.snapshotCevabiKur();
                if (snapshotCevabi) {
                    // parse()/setCachedItem BURADA try içinde: bu blok Promise
                    // executor'ının içinde ve bir throw dışarı sızarsa
                    // executor'ın kendi promise'i reddolur ama `run()`ın
                    // promise'i NE resolve NE reject edilir — çağıran sonsuza
                    // kadar asılı kalırdı. Hata hâlinde canlı yola devam edilir
                    // (plan §Sınırlar: snapshot hatası zinciri KIRMAZ).
                    try {
                        // Canlı yolun aynısı: gövde `parse()` içinde yerinde
                        // zenginleşir (message/selected), sonra AYNI nesne
                        // cache'e yazılır — ikinci bakış tamamen yerel olur.
                        const parsedResult = await this.parse(snapshotCevabi);
                        // F5: TTL burada `snapshotCevabi`nin KENDİSİYLE hesaplanır — üzerinde
                        // `snapshotCevabiKur()`'un eklediği `snapshotDamgasi`/`snapshotTazeKadar`
                        // etiketleri var, yerel kayıt sunucudaki kaydın taze kaldığı kadar tutulur.
                        await setCachedItem(cacheArgs.key, snapshotCevabi, this.etkinLocalTtl(cacheArgs.ttl, snapshotCevabi));
                        return resolve(parsedResult);
                    } catch (e) {
                        console.warn("UyapRequest: snapshot cevabı işlenemedi, canlıya düşülüyor.", e);
                    }
                }
            }
            this.talepData = data;
            this.execute(data).then((response: AxiosResponse) => {
                this.prepareResponseData(data,response).then((preparedResponse: AxiosResponse) => {
                    //console.log("PREPARED DATA",preparedResponse);
                    if (preparedResponse.data.error && preparedResponse.data.error != "" && preparedResponse.data.errorCode && preparedResponse.data.errorCode != "") {
                        const errObj = preparedResponse.data;
                        // errorCode ancak burada görünür; execute() içinde gövde ham.
                        istekOlcumu.kisitKaydet(errObj.errorCode);
                        istekOlcumu.hataKaydet('uyap', this.metricName());
                        if (!errObj.message) errObj.message = errObj.error;
                        return reject(errObj);
                    }
                    this.parse(preparedResponse.data).then(async (result: C) => {
                        if (cacheArgs && cacheArgs.active === true && preparedResponse.data?.type !== "error")
                            await setCachedItem(cacheArgs.key, preparedResponse.data, this.etkinLocalTtl(cacheArgs.ttl));
                        // Faz 4: cevabın kendi `dosyaOzetDVO`su volatil dosyaId ->
                        // kararlı dosya_ref eşlemesini besler; aynı dosyanın SONRAKİ
                        // sorgusu bağlam olmadan da snapshot zincirine girebilsin.
                        cevaptanOgren(result);
                        this.snapshotaYaz(result);
                        return resolve(result);
                    }).catch((err: Error) => {
                        // parse hatası = UYAP format değişikliğinin ilk işareti.
                        istekOlcumu.hataKaydet('parse', this.metricName());
                        return reject(err);
                    })
                }).catch(preparedError => {
                    istekOlcumu.hataKaydet('cevap', this.metricName());
                    return reject(preparedError);
                });
            }).catch(async (err: AxiosError) => {
                istekOlcumu.hataKaydet('istek', this.metricName());
                return reject(err);
            });
        })
    }

    /**
     * UYAP servisi cevap veremediginde firlatilan mesaj. Metin sabit tutuluyor: cagiran
     * katmanlar (orn. TopluHacizGonderTaskHandler) bu durumda bosuna kotali sorgu atmamak
     * icin mesaji tanimak zorunda.
     */
    public static readonly UYAP_SERVIS_ULASILAMIYOR =
        "UYAP bu isteğe cevap veremiyor; beklenen veri yerine portal giriş sayfası döndü. " +
        "Oturum ve diğer UYAP işlemleri çalışıyorsa sorun eicrapro kaynaklı değildir, " +
        "UYAP tarafındaki servis geçici olarak devre dışıdır. Bir süre sonra tekrar deneyiniz.";

    /**
     * UYAP JSON yerine HTML dondugunde tum sayfa hata mesaji olarak firlatiliyordu; bu metin
     * task.description'a yazilip kullaniciya ham HTML olarak gosteriliyordu. Portal login
     * sayfasi ayirt edilip kisa bir mesaja cevriliyor, taninmayan HTML kirpilarak gecirliyor.
     */
    private static htmlCevapMesaji(govde: string): string {
        // UYAP gecidi, arkadaki servise yonlendiremedigi istekleri SPA'nin statik index.html'ine
        // dusuruyor. DEV-1131 har kayitlarinda oturum ve diger uc noktalar calisirken
        // getIcraTalepEvrakHazirla.uyap 26 dakika boyunca kesintisiz bu sayfayi dondurdu:
        // istek icerigiyle ilgisi yok, tekrar denemek de duzeltmiyor.
        if (/Avukat Portal|ArkSignerJS|avukat\/js\//i.test(govde))
            return UyapRequest.UYAP_SERVIS_ULASILAMIYOR;
        const yazi = govde.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        return yazi.length > 0 ? yazi.slice(0, 500) : "UYAP beklenen cevap yerine HTML sayfa döndürdü.";
    }

    async prepareResponseData(requestData: any , data: AxiosResponse): Promise<AxiosResponse> {
        try {
            if (app.$store.state.eicraproDevMode == false) {
                data = await this.tryForBlob(data);
                //console.log("this.getUrl() != this.ImzaClientAddr)",this.getUrl().substr(0,this.ImzaClientAddr.length) , this.ImzaClientAddr);

                /**
                 * Not: Makbuz indirirken file html olarak geliyor fakat bazen yetkiniz yoktur diye html olarak bi metin geliyorbunu handle etmek  için
                 * burada content dispotion kontrolü yapılıp response içindeki metin throw olarak veriliyor buraya elleme bilgin yoksa emre.- abdullah
                 */

                if (typeof data.headers["content-disposition"] == "undefined" &&
                    data.headers["content-type"] &&
                    data.headers["content-type"].includes("text/html") &&
                    this.getUrl().substr(0,this.ImzaClientAddr.length) == this.ImzaClientAddr){
                    // UYAP kısıtlamayı 429 ile değil, 200 + login HTML'i ile bildiriyor.
                    istekOlcumu.beklenmeyenHtmlKaydet();
                    // status 200 ama login sayfası gelmesi durumunda oturum kontrolü ve tekrar isteği atma koşulu
                    if (!this.htmlRetryDone && !UyapRequest.htmlSessionCheckInProgress) {
                        this.htmlRetryDone = true;
                        UyapRequest.htmlSessionCheckInProgress = true;
                        try {
                            await app.$uyap.KullaniciBilgileri().run({});
                            // Session geçerli, UYAP anlık hata — isteği bir kere daha at
                            UyapRequest.htmlSessionCheckInProgress = false;
                            const retryResponse = await this.execute(requestData);
                            return this.prepareResponseData(requestData, retryResponse);
                        } catch (_) {
                            // Session düşmüş — mevcut recovery mekanizmasını kullan
                            UyapRequest.htmlSessionCheckInProgress = false;
                            return this.uyapSessionCheck(requestData, "prepareResponseData");
                        }
                    }
                    const htmlGovde = data.data instanceof Blob ? await data.data.text() : String(data.data);
                    throw new Error(UyapRequest.htmlCevapMesaji(htmlGovde));
                }
            }
            //this.sessionControl(data);
            data = await this.tryForXml(data);
            data = await this.tryForJson(data);
            return data;
        } catch (e) {
            console.error("error > prepareResponseData ", e);
            // @ts-ignore
            // Kota dolu (PRTL_GNL_100026-1) ve nosessionobject ikisi de kurtarmaya
            // gider: görev hataya düşürülmez, bağlantı gelene kadar beklenir.
            // Kota doluysa ek olarak kullanıcıya dialog + bildirim + ses gösterilir
            // ki diğer oturumlarını kapatıp süreci hızlandırabilsin. Yeniden giriş
            // yeni oturum yaratmıyor (web1.uyap reddediliyor), o yüzden bu güvenli.
            if (kotaDoluHatasiMi(e)) {
                // Kurtarma Durdur ile kesilirse görev açıklaması sebebi yansıtsın.
                UyapRequest.kurtarmaKotaNedeniyle = true;
                kotaUyarisiGoster();
                return await this.uyapSessionCheck(requestData, "prepareResponseData");
            }
            if (e.message == "nosessionobject"
                /*|| e.message == "Yapılmak istenen işlem sırasında hata oluştu. "*/) {
                return await this.uyapSessionCheck(requestData, "prepareResponseData");
            }
            // @ts-ignore
            throw e;
        }
    }

    /** Kurtarma sırasında "yeniden bağlanılıyor" mesajı bir kez gösterilsin. */
    private static kurtarmaMesajiGosterildi = false;
    /** Bu kurtarma kota-dolu yüzünden mi başladı — Durdur'daki görev açıklaması için. */
    private static kurtarmaKotaNedeniyle = false;

    /**
     * Oturum düştüğünde bağlantı gelene kadar bekler. ASLA reddetmez — görev
     * hataya düşürülmez, kaldığı yerde bekler. Denemeler yenidenBaglanmaBeklemesiMs
     * temposuyla, sonsuza dek. Bağlantı gelince askıdaki istek yeniden çalışır.
     */
    /** Bot artık "oto" değilse (Durdur'a basıldı) kurtarma sürdürülmez. */
    private botDurduruldu(): boolean {
        return app.$store.getters.getPlayerDurum !== undefined
            && app.$store.getters.getPlayerDurum !== PlayerDurum.oto;
    }

    /**
     * Arayüzden gelen (bot çalışmayan) istekler için kurtarma deneme tavanı.
     * Kullanıcı tıkladığı işlemin başında sonsuza dek bekletilmez.
     *
     * 3 tur = 3+15+30 ≈ 48 sn. 2 tur (18 sn) yetmiyordu: yeniden giriş
     * vue-imza-app'te betaLogin ile yapılıyor ve kart PIN'i localStorage'da
     * kayıtlı değilse kullanıcıya PIN dialogu çıkıyor. 18 sn'de reddetmek,
     * kullanıcı PIN'ini yazarken isteği hataya düşürüyordu. Bekleme süresince
     * UYAP'a bu taraftan hiç istek gitmiyor, tek maliyet kullanıcının beklemesi.
     */
    private static readonly UI_KURTARMA_DENEME = 3;

    private waitForSessionRecovery(): Promise<void> {
        return new Promise(async (resolve, reject) => {
            // Bot çalışırken düşen oturum: görev hataya düşürülmez, sonsuza dek
            // paced denenir (DEV-2447 amacı). Bot çalışmıyorsa istek arayüzden
            // gelmiştir; kullanıcı sonsuza dek bekletilmez, sınırlı denenir.
            const botIstegi = !this.botDurduruldu();
            let deneme = 0;
            let baglandi = false;

            // do-while: bayrak ne derse desin turda bir kez connectToUyap yayınlanır
            // ve EN AZ bir bekleme yapılır. Bu blok eskiden `while (isUyapConnected
            // !== true)` içindeydi; bayrak bayat-true kaldığında hiç çalışmıyor,
            // kurtarma bedelsiz resolve edip execute'u gecikmesiz yeniden çağırıyordu.
            // IP ban'a yol açan sonsuz döngü buydu.
            do {
                // Bot çalışıyordu ve kullanıcı Durdur'a bastı: döngü kırılır. reject
                // edilir (resolve DEĞİL): resolve olsaydı uyapSessionCheck execute'u
                // yeniden çağırır, o da yine hata alıp buraya döner ve gecikmesiz sıkı
                // döngü oluşurdu. reject ile görev BITTI_HATALI olur; kullanıcı
                // sonradan yeniden başlatabilir.
                if (botIstegi && this.botDurduruldu()) {
                    const mesaj = UyapRequest.kurtarmaKotaNedeniyle
                        ? 'UYAP oturum kotası dolu olduğu için bağlantı yapılamadı, işlem durduruldu.'
                        : 'UYAP bağlantısı kurulamadı, işlem durduruldu.';
                    UyapRequest.kurtarmaMesajiGosterildi = false;
                    UyapRequest.kurtarmaKotaNedeniyle = false;
                    return reject(new Error(mesaj));
                }

                deneme++;
                // Yeniden giriş denemesi budur ve KOŞULSUZ olmalı: connectToUyap
                // socket üzerinden yayınlanır (server tüm istemcilere relay eder),
                // App.vue handler'ı checkUyapSession'ı tetikler.
                app.$socket.client.emit('connectToUyap');

                if (!UyapRequest.kurtarmaMesajiGosterildi) {
                    UyapRequest.kurtarmaMesajiGosterildi = true;
                    durumMesajiGoster('info', 'UYAP bağlantısı yeniden kuruluyor...');
                }

                // Bekleme sırasında bağlantı gelirse erken çıkar. Bot isteğinde
                // Durdur'a basılırsa da tavan süreyi beklemeden çıkar.
                baglandi = await this.baglantiBekle(yenidenBaglanmaBeklemesiMs(deneme), botIstegi);

                if (!botIstegi && !baglandi && deneme >= UyapRequest.UI_KURTARMA_DENEME) {
                    UyapRequest.kurtarmaMesajiGosterildi = false;
                    UyapRequest.kurtarmaKotaNedeniyle = false;
                    return reject(new Error(
                        'UYAP oturumu düştü ve yeniden bağlanılamadı. ' +
                        'İmzacı bağlantınızı kontrol edip tekrar deneyiniz.'
                    ));
                }
            } while (!baglandi);

            // Bağlantı kuruldu: bir sonraki kesintiye yanlış sebep taşınmasın.
            UyapRequest.kurtarmaMesajiGosterildi = false;
            UyapRequest.kurtarmaKotaNedeniyle = false;
            devreKesiciSifirla();
            resolve();
        });
    }

    /**
     * ms boyunca 1 sn aralıkla bağlantıyı yoklar; bağlanırsa erken true döner.
     * Bekleme sırasında Durdur'a basılırsa da erken çıkar (false) — kullanıcı
     * uzun bekleme süresinin dolmasını beklemesin.
     */
    private baglantiBekle(ms: number, botDurumunuIzle: boolean = true): Promise<boolean> {
        return new Promise((resolve) => {
            const bitis = Date.now() + ms;
            const interval = setInterval(() => {
                if (app.$store.getters.isUyapConnected === true) {
                    clearInterval(interval);
                    return resolve(true);
                }
                // Arayüz isteğinde bot zaten "dur" durumundadır; bu bir iptal
                // sinyali değildir, o yüzden yalnızca bot isteklerinde izlenir.
                if ((botDurumunuIzle && this.botDurduruldu()) || Date.now() >= bitis) {
                    clearInterval(interval);
                    return resolve(false);
                }
            }, 1000);
        });
    }

    private runSessionRecovery(): Promise<void> {
        if (UyapRequest.sessionRecoveryLock) {
            return UyapRequest.sessionRecoveryLock;
        }
        const promise = this.waitForSessionRecovery().finally(() => {
            UyapRequest.sessionRecoveryLock = null;
        });
        UyapRequest.sessionRecoveryLock = promise;
        return promise;
    }

    uyapSessionCheck(requestData: any, from: "execute" | "prepareResponseData" = "execute"): Promise<AxiosResponse> {
        // KÖK SEBEP DÜZELTMESİ. Buraya gelindiyse oturum düşmüştür, ama store'daki
        // bayrağı düşüren tek yer checkUyapSession'ın catch'iydi ve o fonksiyonun
        // kendi KullaniciBilgileri isteği de aynı döngüye takıldığı için catch'e hiç
        // girilmiyordu. Bayrak "bağlı" kalınca waitForSessionRecovery bedelsiz
        // resolve edip execute'u gecikmesiz yeniden çağırıyor, saniyede ~14 istek
        // UYAP'a gidiyordu. Bayrağı burada düşürmek kurtarmaya giden dört yolun
        // (401 / nosessionobject / kota dolu / login HTML) tamamını kapsar.
        // Yoklama isteği kurtarmaya girmez; cevabı "oturum yok"tur. Dört kurtarma
        // yolunun (401 / login HTML / kota dolu / nosessionobject) tamamı buradan
        // geçtiği için tek kapı yeterli. Bkz. probe().
        //
        // Bayrağa da DOKUNMAZ: yoklamayı yapan (connectToUyap, checkUyapSessionNew,
        // fastLoginUyap) bağlantı durumunu zaten kendisi yönetiyor. Buradan false
        // yazmak, "oturum geçerli" dalında meta veri yüklerken gelen tek bir 401'in
        // az önce true yapılmış bayrağı ezmesine yol açıyordu.
        if (this.isProbe) {
            return Promise.reject(new Error('UYAP oturumu geçersiz.'));
        }
        app.$store.commit('setUyapConnection', false);
        return this.runSessionRecovery().then(async () => {
            const responseData = await this.execute(requestData);
            if (from === "execute") {
                return responseData;
            }
            return this.prepareResponseData(requestData, responseData);
        });
    }


    protected getResponseType(data: any): ResponseType {
        if (typeof data != 'undefined' && typeof data.download != 'undefined') {
            return 'blob';
        }
        return 'text';
    }

    protected getRequestType(): RequestType {
        return "urlencoded";
    }

    protected getSpinnerMessage(): string {
        return "";
    }

    protected getTimeoutSeconds(): number {
        return 30;
    }

    /**
     * Bu sorgunun snapshot deposundaki ALAN adı (picker `name` değeri) — yoksa null.
     *
     * Sınıf adından TÜRETİLMEZ (`constructor.name` küçültmede değişir; aynı yasak
     * `SnapshotYazici.ALAN_ESLEME` tablosunda da açıkça yazılı). Alt sınıf bu
     * metodu geçersiz kılarak hem tekil snapshot YAZIMINA hem de (B1/B07: kayıt
     * ham gövde taşıyorsa, alan sınırı olmadan) snapshot OKUMASINA katılır.
     */
    protected snapshotAlani(): string | null {
        return null;
    }

    /**
     * Snapshot'tan `parse()` öncesi cevap gövdesi kurar; kurulamıyorsa null.
     *
     * Zincire girmenin üç kapısı var, hepsi geçilmeli:
     *  1. Sınıf bir `snapshotAlani()` bildirmeli.
     *  2. Volatil `dosyaId` kararlı `dosya_ref`e çözülebilmeli (`DosyaRefHafizasi`).
     *  3. Kayıt TAZE olmalı: F5 (9 Eyl 2026 kullanıcı kararı) ile artık aynı
     *     takvim günü değil, SAAT PENCERESİ — `tazelik.tazeMi()`: kaydın
     *     `taze_kadar`'ı (plan frekansına göre sunucu hesaplar) çözülüyorsa o,
     *     yoksa `sonGorulme()`den itibaren sabit 24 saat.
     *
     * B1/B07: eskiden dördüncü bir kapı vardı — kayıt HAM GÖVDE taşımıyorsa
     * `cevapKuruculari` tablosundaki (yalnız 3 alan için yazılmış) bir "kurucu"
     * `entities`'ten cevap gövdesini yeniden inşa etmeye çalışırdı. K4'ten beri
     * yazma ucu (`SnapshotYazici.ts`/`TekilSnapshotYazici.ts`) HER paket için
     * ham gövdeyi KOŞULSUZ taşıdığı için bu yedek yolun çalışacağı "kayıt var
     * ama gövde yok" durumu hiçbir üretim koşumunda OLUŞAMAZ — `cevapKuruculari/`
     * dizini bu yüzden SİLİNDİ (ölü kod, B07). Kayıtta gövde yoksa artık
     * doğrudan `null` dönülür, zincir bugünkü davranışa (IndexedDB -> canlı)
     * düşer.
     */
    private async snapshotCevabiKur(): Promise<any | null> {
        try {
            const alan = this.snapshotAlani();
            if (!alan) {
                return null;
            }

            const talep: any = this.talepData;
            const dosyaRef = dosyaRefCoz(talep?.dosyaId);
            if (!dosyaRef) {
                // Bağlam bilinmiyor (ör. DosyaBul koşmamış bir picker): bugünkü
                // davranışta kalınır, hiçbir yerde hata üretilmez.
                return null;
            }

            const kisiKurumId = Number(talep?.kisiKurumId);
            const gecerliKimlik = Number.isFinite(kisiKurumId) && kisiKurumId > 0;

            const kayit: SnapshotKaydi | null = await SnapshotOkuyucu.get(
                dosyaRef,
                alan,
                gecerliKimlik ? kisiKurumId : null,
                gecerliKimlik ? borclununVergiNosu(kisiKurumId) : null
            );
            if (!kayit) {
                return null;
            }

            // F5: `ayniGunMu()` (takvim günü) yerine `tazeMi()` (saat penceresi) —
            // dosya başı `tazelik.ts` notu. `damga` (sonGorulme) aşağıda hâlâ
            // İP3 kaynak rozeti ve `etkinLocalTtl()`'in düşme yolu için lazım.
            if (!tazeMi(kayit)) {
                return null;
            }
            const damga = sonGorulme(kayit);

            // B1 (K4'ün ikinci yarısı): kayıtta ham gövde varsa cevabın kendisi
            // odur — alan sınırı yok. Gövde parse-SONRASI şekilde saklanır;
            // aşağıdaki parse() ikinci uygulamada zararsızdır (parse'lar
            // yerinde-zenginleştirme yapar, koşulları alan-varlığına bağlıdır
            // — K2 gerçek-mock zinciri güvencesi). `entities boşsa servis
            // edilmez` kuralı gövdeli yolda UYGULANMAZ: gövde "sorgu çalıştı,
            // kayıt yok" cevabını da (sorguSonucu:false) geçerli biçimde taşır.
            // Gövde yoksa (B07: kurucu yedeği kaldırıldı — bkz. yukarı) doğrudan
            // canlıya düşülür.
            const govde: any | null = kayit.govde && typeof kayit.govde === "object" ? kayit.govde : null;
            if (!govde) {
                return null;
            }

            // İP3'ün kaynak rozeti bu iki alanı okur. Cache'e de yazılırlar:
            // ikinci bakış yerelden gelse bile kullanıcı verinin gece koşumundan
            // geldiğini görmeye devam etsin.
            govde.snapshotKaynagi = true;
            govde.snapshotDamgasi = damga;
            // F5: `etkinLocalTtl()`in yerel TTL'i kaydın taze KALDIĞI kadar
            // tutabilmesi için taşınır (kayıt hiçbir aktif planda değilse `null`).
            govde.snapshotTazeKadar = kayit.taze_kadar ?? null;

            return govde;
        } catch (e) {
            // Okuma hatası zinciri KIRMAZ (plan §Sınırlar).
            console.warn("UyapRequest: snapshot katmanı atlandı.", e);

            return null;
        }
    }

    /**
     * Canlı cevabı depoya tekil paket olarak yazar (fire-and-forget).
     *
     * Böylece EKRAN sorgusu da depoya düşer: diğer uygulama onu görür ve gece
     * `FrekansBekcisi` "bugün koştu" diyerek alanı BOŞUNA yeniden koşmaz.
     * Snapshot'tan dönen cevap bu noktaya hiç gelmez (yukarıda resolve edilir),
     * dolayısıyla depoya kendi verisi geri yazılmaz.
     */
    private snapshotaYaz(result: any): void {
        const alan = this.snapshotAlani();
        if (!alan) {
            return;
        }

        // B06 (düzeltme, bkz. yukarıdaki `gorevBaglamindakiDosyalar` docstring'i):
        // yalnız KENDİ isteğimizin dosya_ref'i (talepData.dosyaId'den çözülen)
        // haritada >0 ise atlanır — TOPLU yazıcı (SnapshotYazici) o dosya için
        // görev sonunda zaten yazacaktır. Başka bir dosya için gelen ekran
        // isteği bu kapıdan hiçbir zaman etkilenmez. dosyaRef çözülemiyorsa
        // (bağlam yok, ör. DosyaBul hiç koşmamış bir picker) ESKİ davranışta
        // kalınır: tekil yazım devam eder, sessiz susturma yapılmaz.
        const talep: any = this.talepData;
        const dosyaRef = dosyaRefCoz(talep?.dosyaId);
        if (dosyaRef && (UyapRequest.gorevBaglamindakiDosyalar.get(dosyaRef) ?? 0) > 0) {
            return;
        }

        // Beklenmedik bir hata ekran akışını bloklamasın: await EDİLMEZ.
        TekilSnapshotYazici.yaz(alan, result).catch(() => undefined);
    }

    protected abstract getUrl(): string;


    protected abstract getMockResponse(): string | any;

    protected abstract getMethod(): 'POST' | 'GET';

    //protected abstract getDelayTime():number;
    protected abstract cache(): { active: boolean, ttl: number, key: string }

    protected abstract parse(data: string | Blob | any): Promise<C>;


}



